import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomDetailEnum, UpdateMipKycSettingDto } from '@dealstack/shared/lib/generated';
import { getMIPPersonalCustomDetails } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import {
	IndividualInvestorSettings,
	InvestmentVehicleSettings,
	UpdateCustomDetail,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/types.ts';
import { useUpdateCustomDetail } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/api/useUpdateCustomDetail.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { useMipKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting.ts';
import { updateMipKycSetting } from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';

interface Props {
	mipId: string | undefined;
}

export const useInvestorData = ({ mipId }: Props) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const { data: kycSetting, isLoading: isLoadingKycSetting } = useMipKycSetting(mipId ?? '');

	const { hasPermission } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: defaultToEmptyString(mipId),
		permissionRole: PermissionRole.EDITOR,
	});

	const { data: customDetailsResponse, isLoading: isLoadingCustomDetails } = useQuery({
		queryKey: [API_PATHS.MIP_CUSTOM_DETAIL_GET, mipId],
		queryFn: () => getMIPPersonalCustomDetails(undefined),
		enabled: !!mipId,
	});

	const updateSettingsMutation = useMutation({
		mutationFn: (data: UpdateMipKycSettingDto) => updateMipKycSetting(data),
		onSuccess: () => {
			return queryClient.invalidateQueries({
				queryKey: [API_PATHS.KYC_SETTING_MIP_GET, mipId],
			});
		},
	});

	const updateCustomDetailMutation = useUpdateCustomDetail(
		mipId,
		tMipManage('investor_data.custom_fields.updated_success')
	);

	const [individualSettings, setIndividualSettings] = useState<IndividualInvestorSettings>({
		bankAccountDetails: { checked: false, disabled: false },
		taxResidence: { checked: false, disabled: false },
		taxIdentificationNumber: { checked: false, disabled: false },
		placeOfBirth: { checked: false, disabled: false },
		countryOfBirth: { checked: false, disabled: false },
		employeeId: { checked: false, disabled: false },
		citizenship: { checked: false, disabled: false },
	});

	const [vehicleSettings, setVehicleSettings] = useState<InvestmentVehicleSettings>({
		bankAccountDetails: { checked: false, disabled: false },
		controllingPartyInfo: { checked: false, disabled: false },
	});

	useEffect(() => {
		if (!kycSetting) {
			return;
		}

		const isTaxResidenceChecked = kycSetting.isTaxResidenceRequired ?? false;

		setIndividualSettings({
			bankAccountDetails: {
				checked: kycSetting.isInvestorBankAccountRequired ?? false,
				disabled: false,
			},
			taxResidence: { checked: isTaxResidenceChecked, disabled: false },
			taxIdentificationNumber: {
				checked: kycSetting.isTinRequired ?? false,
				disabled: !isTaxResidenceChecked,
			},
			placeOfBirth: {
				checked: kycSetting.isPlaceOfBirthRequired ?? false,
				disabled: false,
			},
			countryOfBirth: {
				checked: kycSetting.isCountryOfBirthRequired ?? false,
				disabled: false,
			},
			employeeId: {
				checked: kycSetting.isEmployeeIdRequired ?? false,
				disabled: false,
			},
			citizenship: {
				checked: kycSetting.isCitizenshipRequired ?? false,
				disabled: false,
			},
		});

		setVehicleSettings({
			bankAccountDetails: {
				checked: kycSetting.isVehicleBankAccountRequired ?? false,
				disabled: false,
			},
			controllingPartyInfo: {
				checked: kycSetting.isControllingPartyInfoRequired ?? false,
				disabled: false,
			},
		});
	}, [kycSetting]);

	const toggleIndividualSetting = (settingKey: keyof IndividualInvestorSettings) => {
		const setting = individualSettings[settingKey];
		const newValue = !setting.checked;

		setIndividualSettings((prev) => ({
			...prev,
			[settingKey]: {
				...prev[settingKey],
				checked: newValue,
			},

			...(settingKey === 'taxResidence' && !newValue
				? {
						taxIdentificationNumber: {
							...prev.taxIdentificationNumber,
							checked: false,
							disabled: true,
						},
					}
				: {}),

			...(settingKey === 'taxResidence' && newValue
				? {
						taxIdentificationNumber: {
							...prev.taxIdentificationNumber,
							disabled: false,
						},
					}
				: {}),
		}));

		const updateMap: Record<keyof IndividualInvestorSettings, UpdateMipKycSettingDto> = {
			bankAccountDetails: { isInvestorBankAccountRequired: newValue },
			taxResidence: {
				isTaxResidenceRequired: newValue,
				...(!newValue ? { isTinRequired: false } : {}),
			},
			taxIdentificationNumber: { isTinRequired: newValue },
			placeOfBirth: { isPlaceOfBirthRequired: newValue },
			countryOfBirth: { isCountryOfBirthRequired: newValue },
			employeeId: { isEmployeeIdRequired: newValue },
			citizenship: { isCitizenshipRequired: newValue },
		};

		updateSettingsMutation.mutate(updateMap[settingKey]);
	};

	const toggleVehicleSetting = (settingKey: keyof InvestmentVehicleSettings) => {
		const setting = vehicleSettings[settingKey];
		const newValue = !setting.checked;

		setVehicleSettings((prev) => ({
			...prev,
			[settingKey]: {
				...prev[settingKey],
				checked: newValue,
			},
		}));

		const updateMap: Record<keyof InvestmentVehicleSettings, UpdateMipKycSettingDto> = {
			bankAccountDetails: { isVehicleBankAccountRequired: newValue },
			controllingPartyInfo: { isControllingPartyInfoRequired: newValue },
		};

		updateSettingsMutation.mutate(updateMap[settingKey]);
	};

	const toggleCustomFieldChecked = (args: UpdateCustomDetail) => {
		updateCustomDetailMutation.mutate({
			...args,
			triggerInvestorReevaluate: true,
		});
	};

	const individualCustomDetails =
		customDetailsResponse?.payload?.filter(
			(detail) => detail.type === CustomDetailEnum.INDIVIDUAL
		) || [];

	const vehicleCustomDetails =
		customDetailsResponse?.payload?.filter(
			(detail) => detail.type === CustomDetailEnum.VEHICLE
		) || [];

	return {
		individualSettings,
		vehicleSettings,
		toggleIndividualSetting,
		toggleVehicleSetting,
		individualCustomDetails,
		vehicleCustomDetails,
		toggleCustomFieldChecked,
		hasPermission,
		isLoading:
			isLoadingCustomDetails ||
			isLoadingKycSetting ||
			updateSettingsMutation.isPending ||
			updateCustomDetailMutation.isPending,
	};
};
